import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import uuid from 'uuid';
import { Subscription, Occurrence, Recipient, UserRecipient } from './types';
import { SubscriptionList } from './SubscriptionList';
import { Button } from '../../Button/Button';
import { stores } from '../../../stores';
import { listOfAdministrators } from '../../../api/relevant';
import { ShareSearchbar } from './ShareSearchbar';
import { Banner } from '../../Banner';

export function SubscriptionsEditor({ subscriptions, onSubscriptionsChange, hasDynamicDateRange, isDirty, setIsDirty }: {
    subscriptions: Subscription[];
    onSubscriptionsChange: (subs: Subscription[]) => void;
    hasDynamicDateRange: boolean;
	isDirty: boolean;
	setIsDirty: (state: boolean) => void;
}) {
	// State of ShareSearchbar
	const currentUser = stores.identity.user();
	const [users, setUsers] = useState<UserRecipient[]>([]);
	const [newRecipients, setNewRecipients] = React.useState<Recipient[]>([]);
	const [newOccurrence, setNewOccurrence] = React.useState<Occurrence>('weekly');
	// State of SubscriptionList
	const [existingSubs, setExistingSubscriptions] = React.useState(subscriptions);

	useEffect(() => {
		const getAdmins = async () => {
			const { result } = await listOfAdministrators();
			const admins = result.map((availableUser) => ({
				id: availableUser.id,
				firstName: availableUser.firstname,
				lastName: availableUser.lastname,
				email: availableUser.email,
			}));
			setUsers(admins);
		};
		// Admins can add subscriptions to themselves and other admins
		if (stores.identity.isAdministrator()) {
			getAdmins();
		} else { // Normal users can only add subscriptions to themselves
			setUsers([{
				id: currentUser.id,
				firstName: currentUser.firstname,
				lastName: currentUser.lastname,
				email: currentUser.email,
			}]);
		}
	}, [currentUser.email, currentUser.firstname, currentUser.id, currentUser.lastname]);
	useEffect(() => setExistingSubscriptions(subscriptions), [subscriptions]);

	const handleSave = () => {
		const newSubs: Subscription[] = newRecipients.map((recipient) => ({
			occurrence: newOccurrence,
			recipient,
			id: uuid.v4(),
			addedByUserId: stores.identity.user().id,
		}));
		onSubscriptionsChange(existingSubs.concat(newSubs));
		// Restore default state
		setIsDirty(false);
		setNewRecipients([]);
		setNewOccurrence('weekly');
	};

	// For ShareSearchBar
	const handleRecipientsChange = (recipients: Recipient[]) => {
		setNewRecipients(recipients);
		if (recipients.length > 0) {
			setIsDirty(true);
		} else {
			setIsDirty(!_.isEqual(subscriptions, existingSubs));
		}
	};
	// For SubscriptionList
	const handleDelete = (subscriptionId: string) => {
		const updated = existingSubs.filter((sub) => sub.id !== subscriptionId);
		setExistingSubscriptions(updated);
		setIsDirty(newRecipients.length > 0 || !_.isEqual(subscriptions, updated));
	};
	const handleChangeOccurrence = (subscriptionId: string, occurrence: Occurrence) => {
		const updated = existingSubs.map((sub) => {
			if (sub.id === subscriptionId) {
				return { ...sub, occurrence };
			}
			return sub;
		});
		setExistingSubscriptions(updated);
		setIsDirty(newRecipients.length > 0 || !_.isEqual(subscriptions, updated));
	};

	return (
		<div className="flex flex-col p-6 items-center gap-6 self-stretch">
			{!hasDynamicDateRange && (
				<Banner
					status="warning"
					message="With fixed dates set, your report will keep exporting the same data. Consider changing to a dynamic date range."
				/>
			)}
			<span className="self-stretch text-sm text-text-secondary font-normal tracking-[0.14px] w-full">
				The following users receive scheduled e-mails containing an updated version of this report, based on the dynamic date range selected.
			</span>
			<div className="flex flex-col items-start gap-2 self-stretch">
				<span className="flex items-center self-stretch text-base font-medium text-text-primary tracking-[0.16px]">
					New Recipients
				</span>
				<ShareSearchbar
					users={users}
					recipients={newRecipients}
					onRecipientsChange={handleRecipientsChange}
					initialOccurrence={newOccurrence}
					onOccurrenceSelect={setNewOccurrence}
				/>
			</div>
			<Button
				onMouseDown={handleSave}
				className="flex py-2.5 px-6 justify-center items-center gap-2 self-stretch rounded-sm disabled:bg-component-button-brand-bg-filled_disabled"
				disabled={!isDirty}
			>
				Save subscriptions
			</Button>
			<div className="w-full h-[1px] bg-borders-tertiary" />
			<SubscriptionList
				subscriptions={existingSubs}
				onDeleteSubscription={handleDelete}
				onChangeOccurrence={handleChangeOccurrence}
			/>
		</div>
	);
}
